import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faFilter,
  faTasks,
  faBell,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

import { taskService } from '../../services/taskService';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';
import TaskList from './TaskList';
import CreateTaskModal from './CreateTaskModal';
import TaskDetailsModal from './TaskDetailsModal';
import NotificationPanel from './NotificationPanel';
import './TaskManagement.css';

const TaskManagement = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    search: ''
  });
  const [activeView, setActiveView] = useState('all'); // all, my, created
  
  // Load initial data
  useEffect(() => {
    loadTasks();
    loadNotifications();
  }, [filters, activeView]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      let tasksData;
      
      if (activeView === 'my') {
        tasksData = await taskService.getMyTasks(filters.status);
      } else if (activeView === 'created') {
        tasksData = await taskService.getTasks({ 
          ...filters, 
          created_by: user.id 
        });
      } else {
        tasksData = await taskService.getTasks(filters);
      }
      
      setTasks(tasksData.tasks || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      // Don't show error toast for auth issues
      if (error.response?.status === 401) {
        setTasks([]);
      } else {
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const notificationsData = await taskService.getNotifications(true);
      setNotifications(notificationsData.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Don't show error toast for auth issues, just set empty array
      if (error.response?.status === 401) {
        setNotifications([]);
      } else {
        toast.error('Failed to load notifications');
      }
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      await taskService.createTask(taskData);
      toast.success('Task created successfully!');
      setShowCreateModal(false);
      loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleTaskUpdate = async (taskId, updateData) => {
    try {
      await taskService.updateTask(taskId, updateData);
      toast.success('Task updated successfully!');
      loadTasks();
      
      // Refresh task details if currently viewing
      if (selectedTask?.id === taskId) {
        const updatedTask = await taskService.getTask(taskId);
        setSelectedTask(updatedTask);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleTaskStatusUpdate = async (taskId, status) => {
    try {
      await taskService.updateTaskStatus(taskId, status);
      toast.success(`Task status updated to ${status}!`);
      loadTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const handleTaskClick = async (task) => {
    try {
      const taskDetails = await taskService.getTask(task.id);
      setSelectedTask(taskDetails);
      setShowTaskDetails(true);
    } catch (error) {
      console.error('Error loading task details:', error);
      toast.error('Failed to load task details');
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await taskService.markNotificationRead(notification.id);
      
      // Load and show the related task
      const task = await taskService.getTask(notification.task_id);
      setSelectedTask(task);
      setShowTaskDetails(true);
      setShowNotifications(false);
      
      // Refresh notifications
      loadNotifications();
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Failed to load task from notification');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-gray-600 bg-gray-100',
      todo: 'text-gray-600 bg-gray-100',
      in_progress: 'text-blue-600 bg-blue-100',
      completed: 'text-green-600 bg-green-100',
      cancelled: 'text-red-600 bg-red-100',
      timeout: 'text-red-600 bg-red-100'
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      urgent: 'text-red-600'
    };
    return colors[priority] || colors.medium;
  };

  const getTaskStats = () => {
    const stats = {
      total: tasks.length,
      todo: tasks.filter(t => t.status === 'todo').length,
      in_progress: tasks.filter(t => t.status === 'in_progress').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: tasks.filter(t => {
        const dueDate = new Date(t.due_date);
        return t.due_date && dueDate < new Date() && t.status !== 'completed';
      }).length
    };
    return stats;
  };

  const stats = getTaskStats();

  return (
    <MainLayout>
      <div className="task-management-container">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="task-header">
          <div className="flex items-center justify-between">
            <div className="header-text">
              <h1 className="text-3xl font-bold text-white flex items-center">
                <FontAwesomeIcon icon={faTasks} className="mr-3" />
                Task Management
              </h1>
              <p className="text-lg text-white/90 mt-1">
                Manage and track your tasks efficiently
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className={`notification-bell ${notifications.length > 0 ? 'has-notifications' : ''}`}
                >
                  <FontAwesomeIcon icon={faBell} />
                  {notifications.length > 0 && (
                    <span className="notification-badge">
                      {notifications.length > 99 ? '99+' : notifications.length}
                    </span>
                  )}
                </button>
                
                <AnimatePresence>
                  {showNotifications && (
                    <NotificationPanel
                      notifications={notifications}
                      onClose={() => setShowNotifications(false)}
                      onNotificationClick={handleNotificationClick}
                      onRefresh={loadNotifications}
                    />
                  )}
                </AnimatePresence>
              </div>
              
              {/* Create Task Button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                New Task
              </button>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="task-stats">
            <div className="stat-card">
              <div className="stat-icon bg-blue-100 text-blue-600">
                <FontAwesomeIcon icon={faTasks} />
              </div>
              <div>
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Tasks</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon bg-yellow-100 text-yellow-600">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div>
                <div className="stat-number">{stats.in_progress}</div>
                <div className="stat-label">In Progress</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon bg-green-100 text-green-600">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div>
                <div className="stat-number">{stats.completed}</div>
                <div className="stat-label">Completed</div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon bg-red-100 text-red-600">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div>
                <div className="stat-number">{stats.overdue}</div>
                <div className="stat-label">Overdue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Views */}
        <div className="task-controls">
          {/* View Tabs */}
          <div className="view-tabs">
            <button
              onClick={() => setActiveView('all')}
              className={`view-tab ${activeView === 'all' ? 'active' : ''}`}
            >
              All Tasks
            </button>
            <button
              onClick={() => setActiveView('my')}
              className={`view-tab ${activeView === 'my' ? 'active' : ''}`}
            >
              My Tasks
            </button>
            <button
              onClick={() => setActiveView('created')}
              className={`view-tab ${activeView === 'created' ? 'active' : ''}`}
            >
              Created by Me
            </button>
          </div>
          
          {/* Filters */}
          <div className="task-filters">
            <div className="filter-group">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="filter-select"
              >
                <option value="">All Status</option>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                className="filter-select"
              >
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="search-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-hidden">
          <TaskList
            tasks={tasks}
            loading={loading}
            onTaskClick={handleTaskClick}
            onStatusUpdate={handleTaskStatusUpdate}
            onTaskUpdate={handleTaskUpdate}
            getStatusColor={getStatusColor}
            getPriorityColor={getPriorityColor}
          />
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateTaskModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateTask}
          />
        )}
        
        {showTaskDetails && selectedTask && (
          <TaskDetailsModal
            task={selectedTask}
            onClose={() => setShowTaskDetails(false)}
            onUpdate={handleTaskUpdate}
            onStatusUpdate={handleTaskStatusUpdate}
          />
        )}
      </AnimatePresence>
      </div>
    </MainLayout>
  );
};

export default TaskManagement;